/**
 * Sampled instrument definitions, read from the shipped manifest.
 *
 * These used to be hand-written lists of note names that had to be kept in step
 * with whatever was actually in `public/audio/`. They drifted immediately: the
 * guitar entry named eight notes that had never been downloaded, and the app
 * confidently asked for every one of them.
 *
 * `public/audio/manifest.json` is generated from the files on disk by
 * `scripts/build-audio-manifest.mjs`, so it cannot describe a sample that is not
 * there. It carries the byte size, checksum, codec and instrument version the
 * service worker needs to install a *complete* set and tell a finished download
 * from a half-finished one.
 *
 * Samples are served from the app's own origin, never hotlinked.
 */

import manifest from '../../public/audio/manifest.json';

export type InstrumentId = 'piano' | 'guitar';

export interface SampleEntry {
  /** Scientific pitch, e.g. `A#4`. */
  note: string;
  /** File name under the instrument's folder. `#` is written `s` in the path. */
  file: string;
  bytes: number;
  sha256: string;
  codec: string;
  mime: string;
}

export interface SampledInstrument {
  id: InstrumentId;
  label: string;
  /** Folder under the audio base. */
  folder: string;
  /** The recording set's own version, not the app's. */
  version: string;
  entries: SampleEntry[];
  /** Seconds of release tail to model. */
  release: number;
  attribution: {
    name: string;
    author: string;
    license: string;
    source: string;
  };
}

/** How each instrument should sound and be credited. Everything else is measured. */
const CHARACTER: Record<
  InstrumentId,
  { label: string; release: number; attribution: SampledInstrument['attribution'] }
> = {
  piano: {
    label: 'Piano',
    release: 1.4,
    attribution: {
      name: 'Salamander Grand Piano V3',
      author: 'Alexander Holm',
      license: 'CC BY 3.0',
      source: 'https://github.com/sfzinstruments/SalamanderGrandPiano',
    },
  },
  guitar: {
    label: 'Acoustic guitar',
    // A nylon string rings on well past the note that follows it. Cutting the
    // tail short is the single thing that makes a sampled guitar sound like a
    // keyboard patch.
    release: 1.9,
    attribution: {
      name: 'Spanish Classical Guitar (Nylon-String Acoustic Guitar)',
      author: 'FreePats — roberto@zenvoid.org',
      license: 'CC0 1.0',
      source: 'https://freepats.zenvoid.org/Guitar/acoustic-guitar.html',
    },
  },
};

function build(id: InstrumentId): SampledInstrument {
  const described = manifest.instruments.find((i) => i.instrument === id);
  if (!described) throw new Error(`No audio manifest entry for ${id}`);
  const character = CHARACTER[id];
  return {
    id,
    label: character.label,
    folder: id,
    version: described.version,
    release: character.release,
    attribution: character.attribution,
    entries: described.samples as SampleEntry[],
  };
}

export const INSTRUMENTS: Record<InstrumentId, SampledInstrument> = {
  piano: build('piano'),
  guitar: build('guitar'),
};

export const INSTRUMENT_IDS: InstrumentId[] = ['piano', 'guitar'];

/**
 * The manifest's version, which is the hash of the audio it describes.
 *
 * Not a build number: that would change when nothing about the audio had, and
 * every one of those changes would evict a learner's working offline cache for
 * nothing.
 */
export const AUDIO_MANIFEST_VERSION: string = manifest.version;

/** Where sample folders live. Point at R2 by setting VITE_AUDIO_BASE at build time. */
export function audioBase(): string {
  const configured = import.meta.env?.VITE_AUDIO_BASE;
  return typeof configured === 'string' && configured.length
    ? configured.replace(/\/$/, '')
    : '/audio';
}

/**
 * `Tone.Sampler`'s `urls` map: recorded note -> file name.
 *
 * The note keys are the real scientific pitch — `A#4`, not `As4` — because that
 * is what Tone parses. The `s` spelling exists only so the file name is a legal
 * URL path segment.
 */
export function sampleUrls(instrument: SampledInstrument): Record<string, string> {
  return Object.fromEntries(instrument.entries.map((entry) => [entry.note, entry.file]));
}

export function sampleBaseUrl(instrument: SampledInstrument): string {
  return `${audioBase()}/${instrument.folder}/`;
}

/** Every sample URL for an instrument, absolute from the app's root. */
export function sampleUrlList(instrument: SampledInstrument): string[] {
  return instrument.entries.map((entry) => `${sampleBaseUrl(instrument)}${entry.file}`);
}
